/**
 * ActionBuilder - Fluent API for building proposals with type-safe actions
 *
 * Provides a chainable interface for constructing CreateProposalRequest objects
 * with validation and auto-incrementing execution order.
 */

import { InvalidActionPayloadError, InvalidProposalError } from './errors';
import type {
  CreateProposalRequest,
  GmailSendParams,
  GmailSendPayload,
  ProposalActionInput,
  SourceContext,
  TimelineEventPayload,
} from './types';

// ============================================================================
// ProposalBuilder Class
// ============================================================================

/** Internal builder for constructing proposal creation requests. Used by `quickEmailProposal`. */
class ProposalBuilder {
  private title?: string;
  private description?: string;
  private assignedUserId?: number;
  private sourceTypeName?: string;
  private sourceId?: string;
  private sourceContext?: SourceContext;
  private actions: ProposalActionInput[] = [];
  private nextExecutionOrder = 0;

  /**
   * Create a new proposal builder
   */
  static create(): ProposalBuilder {
    return new ProposalBuilder();
  }

  /**
   * Set proposal title
   *
   * @param title - Proposal title (max 255 chars)
   * @returns this builder for chaining
   */
  setTitle(title: string): this {
    if (!title || title.trim().length === 0) {
      throw new InvalidProposalError('Title cannot be empty', {
        title: ['This field is required and cannot be blank'],
      });
    }

    if (title.length > 255) {
      throw new InvalidProposalError('Title is too long', {
        title: ['Title cannot exceed 255 characters'],
      });
    }

    this.title = title;
    return this;
  }

  /**
   * Set proposal description (optional)
   *
   * @param description - Proposal description
   * @returns this builder for chaining
   */
  setDescription(description: string): this {
    this.description = description;
    return this;
  }

  /**
   * Set assigned user ID
   *
   * @param userId - User ID to assign proposal to
   * @returns this builder for chaining
   */
  setAssignedUser(userId: number): this {
    if (!Number.isInteger(userId) || userId <= 0) {
      throw new InvalidProposalError('Invalid user ID', {
        assigned_user_id: ['Must be a positive integer'],
      });
    }

    this.assignedUserId = userId;
    return this;
  }

  /**
   * Set source type and ID
   *
   * @param sourceTypeName - Source type (e.g., 'ahoy_user_agent_conversation')
   * @param sourceId - Source identifier
   * @returns this builder for chaining
   *
   * @example
   * ```typescript
   * builder.setSource('ahoy_user_agent_conversation', 'conv-123');
   * ```
   */
  setSource(sourceTypeName: string, sourceId: string): this {
    if (!sourceTypeName || sourceTypeName.trim().length === 0) {
      throw new InvalidProposalError('Source type name cannot be empty', {
        source_type_name: ['This field is required'],
      });
    }

    if (sourceTypeName.length > 100) {
      throw new InvalidProposalError('Source type name is too long', {
        source_type_name: ['Cannot exceed 100 characters'],
      });
    }

    if (!sourceId || sourceId.trim().length === 0) {
      throw new InvalidProposalError('Source ID cannot be empty', {
        source_id: ['This field is required'],
      });
    }

    if (sourceId.length > 255) {
      throw new InvalidProposalError('Source ID is too long', {
        source_id: ['Cannot exceed 255 characters'],
      });
    }

    this.sourceTypeName = sourceTypeName;
    this.sourceId = sourceId;
    return this;
  }

  /**
   * Set source context (channel, account identifier, etc.)
   *
   * @param context - Source context object
   * @returns this builder for chaining
   *
   * @example
   * ```typescript
   * builder.setSourceContext({
   *   channel: 'ahoy_crm',
   *   account_identifier: 'user@example.com',
   * });
   * ```
   */
  setSourceContext(context: SourceContext): this {
    this.sourceContext = context;
    return this;
  }

  /**
   * Add a Gmail send action
   *
   * @param params - Gmail send parameters
   * @returns this builder for chaining
   *
   * @example
   * ```typescript
   * builder.addGmailAction({
   *   to: ['user@example.com'],
   *   subject: 'Hello',
   *   body: 'Email body',
   *   thread_id: 'thread-123', // Optional - for replies
   *   cc: ['cc@example.com'], // Optional
   * });
   * ```
   */
  addGmailAction(params: GmailSendParams): this {
    this.validateGmailParams(params);

    const payload: GmailSendPayload = {
      action_params: params,
    };

    this.actions.push({
      execution_order: this.nextExecutionOrder++,
      proposed_action_type_name: 'gmail_send',
      payload,
    });

    return this;
  }

  /**
   * Add a timeline event action
   *
   * @param params - Timeline event parameters
   * @returns this builder for chaining
   *
   * @example
   * ```typescript
   * builder.addTimelineEvent({
   *   entity_id: 'contact-uuid',
   *   entity_type_name: 'ahoy_contact',
   *   event_type_name: 'ahoy_property_updated',
   *   payload: {
   *     property_name: 'ahoy_status',
   *     new_value: 'active',
   *   },
   * });
   * ```
   */
  addTimelineEvent(params: TimelineEventPayload): this {
    this.validateTimelineEventParams(params);

    this.actions.push({
      execution_order: this.nextExecutionOrder++,
      proposed_action_type_name: 'timeline_event',
      payload: params,
    });

    return this;
  }

  /**
   * Add a custom action with manual payload (advanced usage)
   *
   * @param action - Complete action input
   * @returns this builder for chaining
   */
  addCustomAction(action: Omit<ProposalActionInput, 'execution_order'>): this {
    this.actions.push({
      ...action,
      execution_order: this.nextExecutionOrder++,
    });

    return this;
  }

  /**
   * Reset execution order (useful for testing or rebuilding)
   *
   * @returns this builder for chaining
   */
  resetExecutionOrder(): this {
    // Update all existing actions
    this.actions.forEach((action, index) => {
      action.execution_order = index;
    });
    // Set next execution order to continue after existing actions
    this.nextExecutionOrder = this.actions.length;
    return this;
  }

  /**
   * Validate and build the final CreateProposalRequest
   *
   * @returns CreateProposalRequest ready for API submission
   * @throws InvalidProposalError if required fields are missing
   *
   * @example
   * ```typescript
   * const request = builder.build();
   * const proposal = await client.create(request);
   * ```
   */
  build(): CreateProposalRequest {
    const errors: Record<string, string[]> = {};

    // Validate required fields
    if (!this.title) {
      errors['title'] = ['This field is required'];
    }

    if (this.assignedUserId === undefined) {
      errors['assigned_user_id'] = ['This field is required'];
    }

    if (!this.sourceTypeName) {
      errors['source_type_name'] = ['This field is required'];
    }

    if (!this.sourceId) {
      errors['source_id'] = ['This field is required'];
    }

    if (this.actions.length === 0) {
      errors['actions'] = ['At least one action is required'];
    }

    if (Object.keys(errors).length > 0) {
      throw new InvalidProposalError(
        'Cannot build proposal: missing required fields',
        errors
      );
    }

    // At this point validation passed — all required fields are guaranteed to be set.
    // Use nullish coalescing with empty fallbacks (unreachable due to the throw above).
    const request: CreateProposalRequest = {
      title: this.title ?? '',
      description: this.description,
      assigned_user_id: this.assignedUserId ?? 0,
      source_type_name: this.sourceTypeName ?? '',
      source_id: this.sourceId ?? '',
      actions: [...this.actions], // Return a copy to prevent external mutation
    };

    if (this.sourceContext) {
      request.source_context = this.sourceContext;
    }

    return request;
  }

  /**
   * Get current actions array (useful for debugging)
   */
  getActions(): readonly ProposalActionInput[] {
    return [...this.actions];
  }

  /**
   * Clear all actions
   *
   * @returns this builder for chaining
   */
  clearActions(): this {
    this.actions = [];
    this.nextExecutionOrder = 0;
    return this;
  }

  // ============================================================================
  // Private Validation Helpers
  // ============================================================================

  private validateGmailParams(params: GmailSendParams): void {
    const errors: Record<string, string[]> = {};

    // Validate required fields
    if (!params.to || !Array.isArray(params.to) || params.to.length === 0) {
      errors['to'] = ['At least one recipient is required'];
    } else {
      // Validate email formats
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const invalidEmails = params.to.filter(email => !emailRegex.test(email));
      if (invalidEmails.length > 0) {
        errors['to'] = [`Invalid email addresses: ${invalidEmails.join(', ')}`];
      }
    }

    if (!params.subject || params.subject.trim().length === 0) {
      errors['subject'] = ['Subject is required'];
    }

    if (!params.is_reply && (!params.body || params.body.trim().length === 0)) {
      errors['body'] = ['Body is required'];
    }

    // Validate optional fields
    if (
      params.thread_id !== undefined &&
      typeof params.thread_id !== 'string'
    ) {
      errors['thread_id'] = ['Thread ID must be a string'];
    }

    if (params.cc !== undefined) {
      if (!Array.isArray(params.cc)) {
        errors['cc'] = ['CC must be an array of email addresses'];
      } else {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const invalidEmails = params.cc.filter(
          email => !emailRegex.test(email)
        );
        if (invalidEmails.length > 0) {
          errors['cc'] = [
            `Invalid CC email addresses: ${invalidEmails.join(', ')}`,
          ];
        }
      }
    }

    if (params.bcc !== undefined) {
      if (!Array.isArray(params.bcc)) {
        errors['bcc'] = ['BCC must be an array of email addresses'];
      } else {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const invalidEmails = params.bcc.filter(
          email => !emailRegex.test(email)
        );
        if (invalidEmails.length > 0) {
          errors['bcc'] = [
            `Invalid BCC email addresses: ${invalidEmails.join(', ')}`,
          ];
        }
      }
    }

    if (Object.keys(errors).length > 0) {
      throw new InvalidActionPayloadError('gmail_send', errors);
    }
  }

  private validateTimelineEventParams(params: TimelineEventPayload): void {
    const errors: Record<string, string[]> = {};

    // Validate required fields
    if (!params.entity_id || params.entity_id.trim().length === 0) {
      errors['entity_id'] = ['Entity ID is required'];
    }

    if (
      !params.entity_type_name ||
      params.entity_type_name.trim().length === 0
    ) {
      errors['entity_type_name'] = ['Entity type name is required'];
    }

    if (!params.event_type_name || params.event_type_name.trim().length === 0) {
      errors['event_type_name'] = ['Event type name is required'];
    }

    if (!params.payload || typeof params.payload !== 'object') {
      errors['payload'] = ['Event payload is required and must be an object'];
    }

    if (Object.keys(errors).length > 0) {
      throw new InvalidActionPayloadError('timeline_event', errors);
    }
  }
}

// ============================================================================
// Convenience Factory Functions
// ============================================================================

/**
 * Create a quick single-action proposal
 *
 * @param params - Proposal parameters
 * @param params.isReply - Whether this email is a reply to an existing thread
 * @param params.replyToMessageId - Message ID of the email being replied to
 * @param params.socialAccountId - Social account ID to send from (multi-account OAuth)
 * @returns CreateProposalRequest
 *
 * @example
 * ```typescript
 * const request = quickEmailProposal({
 *   title: 'Welcome email',
 *   assignedUserId: 42,
 *   sourceTypeName: 'ahoy_user_agent_conversation',
 *   sourceId: 'conv-123',
 *   to: ['user@example.com'],
 *   subject: 'Welcome!',
 *   body: 'Thanks for signing up',
 * });
 * ```
 */
export function quickEmailProposal(params: {
  title: string;
  description?: string;
  assignedUserId: number;
  sourceTypeName: string;
  sourceId: string;
  sourceContext?: SourceContext;
  to: string[];
  subject: string;
  body: string;
  threadId?: string;
  cc?: string[];
  bcc?: string[];
  isReply?: boolean;
  replyToMessageId?: string;
  socialAccountId?: number;
}): CreateProposalRequest {
  const builder = ProposalBuilder.create()
    .setTitle(params.title)
    .setDescription(params.description || '')
    .setAssignedUser(params.assignedUserId)
    .setSource(params.sourceTypeName, params.sourceId)
    .addGmailAction({
      to: params.to,
      subject: params.subject,
      body: params.body,
      thread_id: params.threadId,
      cc: params.cc,
      bcc: params.bcc,
      is_reply: params.isReply,
      reply_to_message_id: params.replyToMessageId,
      social_account_id: params.socialAccountId,
    });

  if (params.sourceContext) {
    builder.setSourceContext(params.sourceContext);
  }

  return builder.build();
}
